import {getUnitTypesForRecruitment} from "../../unit/getUnitTypesForRecruitment";
import {getSizeRecruitmentPrice} from "../../recruitment/getSizeRecruitmentPrice";
import {getUnitTypeConfig} from "../../../configs/units";
import {getAvailableUnitUpgrades} from "../../unit/getAvailableUnitUpgrades";
import {checkIfUnitTypeChangeExceedsLimit} from "./checkIfUnitTypeChangeExceedsLimit";

/**
 * Самое дешевое улучшение типа, доступное отрядам на рабочих казармах, — по тем
 * же правилам, что и модалка улучшения (UnitUpgradeModal): варианты с учетом
 * фракции самого отряда, без тех, что упираются в лимит отрядов одного типа
 * (такие модалка приглушает и не дает выбрать). null — улучшать нечего
 */
const calcCheapestUnitUpgradePrice = (player, upgradeCandidates, units) => {
  const prices = upgradeCandidates.flatMap((unit) => getAvailableUnitUpgrades(unit.type, unit.factionId)
    .filter((option) => !checkIfUnitTypeChangeExceedsLimit(unit.id, option.toId, player, units))
    .map((option) => option.price));

  return prices.length > 0 ? Math.min(...prices) : null;
};

/**
 * Минимум монет, за который игрок ещё может что-то сделать на этапе найма:
 * - нанять новый отряд самого дешёвого доступного ему типа (с учётом
 *   фракционных ограничений найма и скидок политик) — кроме последнего раунда,
 *   где новых отрядов не нанимают;
 * - докупить +1 размера самому дешёвому из своих отрядов на рабочей казарме с
 *   окном докупки (sizeUpgradableUnits);
 * - улучшить тип своему отряду на рабочей казарме (upgradeCandidates).
 *
 * Докупка и улучшение считаются, только если такой отряд действительно есть —
 * сама по себе невысокая цена ничего не значит. Раньше в этом месте была
 * захардкоженная цена d6 (2 монеты) — ломалось для фракций без d6 в найме
 * (великаны нанимают только d20 за 10 монет).
 *
 * @param {Object} player
 * @param {Object} [options]
 * @param {Array} [options.sizeUpgradableUnits] — свои отряды на рабочих казармах с окном докупки размера
 * @param {Array} [options.upgradeCandidates] — свои отряды на рабочих казармах (кандидаты на улучшение)
 * @param {Array} [options.units] — все отряды партии: для лимита отрядов одного типа при улучшении
 * @param {boolean} [options.canRecruitNewUnits] — можно ли нанимать новые отряды (в последнем раунде нельзя)
 * @returns {number} Infinity — на этапе найма сделать нечего
 */
export const calcRecruitmentStageMinCoins = (player, {
  sizeUpgradableUnits = [],
  upgradeCandidates = [],
  units = [],
  canRecruitNewUnits = true,
} = {}) => {
  const prices = [];

  if (canRecruitNewUnits) {
    const availableUnitTypes = getUnitTypesForRecruitment(player);
    if (availableUnitTypes.length > 0) {
      prices.push(Math.min(...availableUnitTypes.map(unitType => unitType.price)));
    }
  }

  if (sizeUpgradableUnits.length > 0) {
    prices.push(Math.min(
      ...sizeUpgradableUnits.map(unit => {
        const unitTypeConfig = getUnitTypeConfig(unit.type);
        return getSizeRecruitmentPrice(unitTypeConfig, player).sizePrice;
      })
    ));
  }

  const cheapestUpgradePrice = calcCheapestUnitUpgradePrice(player, upgradeCandidates, units);
  if (cheapestUpgradePrice !== null) {
    prices.push(cheapestUpgradePrice);
  }

  return prices.length > 0 ? Math.min(...prices) : Infinity;
};
